/**
 * インスペクタ用の原本音源ハンドラ。
 *
 * 「波形をどう描くか」と「選択範囲をどう切り出すか」を 1 つのインターフェースに
 * まとめ、素材に応じて 3 経路を自動で選ぶ。
 *
 * - wav-full  : WAV かつ小さめ → 全体を保持し、バイトコピーで切り出す（無変換）
 * - wav-range : WAV かつ大きい → ヘッダだけ保持し、書き出し時に Range 取得（無変換）
 * - decoded   : 非 WAV → decodeAudioData して WAV 24bit で再エンコード
 *
 * 取得中は Lambda 生成の peaks JSON（20 点/秒）を先に描き、原本が届いたら
 * 高密度の peaks に差し替える。選択が切り替わったら前回分は abort して破棄する。
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { sliceAudioBufferToWav } from './audioClip';
import { WaveformPeaks, computePeaksFromAudioBuffer, fetchPeaksJson } from './peaks';
import {
  WAV_HEADER_BYTES,
  WAV_HEADER_PROBE_BYTES,
  WavHeader,
  buildWavBlobFromPcm,
  frameAt,
  parseWavHeader,
  peaksFromPcm,
  rangeForSelection,
  sliceWavFromFile,
} from './wavSource';

export type AudioSourceMode = 'wav-full' | 'wav-range' | 'decoded';

/** 全体をメモリに載せる WAV の上限（24bit/48kHz ステレオで約 100 秒） */
const WAV_FULL_FETCH_MAX_BYTES = 30 * 1024 * 1024;

/** decode 経路に載せる上限（デコード後の Float32 PCM は元の数倍に膨らむ） */
const DECODE_MAX_BYTES = 60 * 1024 * 1024;

/** インスペクタ波形のピーク密度（一覧の 20 点/秒より細かく描く） */
const INSPECTOR_PAIRS_PER_SECOND = 100;

/** decode 経路で書き出す際のビット深度 */
const DECODED_EXPORT_BIT_DEPTH = 24;

interface LoadedSource {
  mode: AudioSourceMode;
  header: WavHeader | null;
  /** wav-full のときだけ保持するファイル全体 */
  fileBytes: ArrayBuffer | null;
  /** decoded のときだけ保持するデコード済みバッファ */
  audioBuffer: AudioBuffer | null;
  /** wav-range のときの取得元 URL */
  url: string;
}

export interface UseAudioSourceOptions {
  /** 原本ファイルの URL（切り出しと高密度波形はこちらから取る） */
  src: string | null;
  /** Lambda 生成 peaks JSON の URL（原本取得までの先出し表示に使う） */
  fallbackPeaksUrl?: string | null;
}

export interface UseAudioSourceResult {
  peaks: WaveformPeaks | null;
  /** 原本から得た総尺（秒）。未取得なら 0 */
  duration: number;
  mode: AudioSourceMode | null;
  /** UI 表示用のフォーマット文字列（例: "WAV 24bit / 48kHz / 2ch"） */
  formatLabel: string | null;
  loading: boolean;
  error: string | null;
  /** 切り出しが可能な状態か */
  ready: boolean;
  /** 選択範囲を切り出した WAV のバイト数見積り */
  estimateBytes: (_startSeconds: number, _endSeconds: number) => number;
  /** 選択範囲を WAV Blob として切り出す */
  sliceToWav: (_startSeconds: number, _endSeconds: number) => Promise<Blob | null>;
}

/**
 * レスポンスヘッダからファイル全体のバイト数を読む。分からなければ null。
 *
 * `Content-Range` は CORS セーフリスト外なので、S3 の ExposeHeaders に
 * 入っていないと 206 でも読めない。読めなかったことを「全部取れた」と
 * 誤認すると先読み分だけを全体とみなしてしまうため、ここでは正直に null を返し、
 * 呼び出し側が WAV ヘッダなど別の根拠で判断できるようにする。
 */
function readTotalSize(response: Response): number | null {
  const contentRange = response.headers.get('content-range');
  if (contentRange) {
    const total = Number(contentRange.split('/')[1]);
    if (Number.isFinite(total) && total > 0) return total;
  }
  if (response.status !== 206) {
    const contentLength = Number(response.headers.get('content-length'));
    if (Number.isFinite(contentLength) && contentLength > 0) return contentLength;
  }
  return null;
}

function wavFormatLabel(header: WavHeader): string {
  const depth =
    header.encoding === 'float' ? `${header.bitsPerSample}bit float` : `${header.bitsPerSample}bit`;
  return `WAV ${depth} / ${header.sampleRate / 1000}kHz / ${header.channels}ch`;
}

export function useAudioSource({
  src,
  fallbackPeaksUrl,
}: UseAudioSourceOptions): UseAudioSourceResult {
  const loadedRef = useRef<LoadedSource | null>(null);
  const [peaks, setPeaks] = useState<WaveformPeaks | null>(null);
  const [duration, setDuration] = useState(0);
  const [mode, setMode] = useState<AudioSourceMode | null>(null);
  const [formatLabel, setFormatLabel] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    loadedRef.current = null;
    setPeaks(null);
    setDuration(0);
    setMode(null);
    setFormatLabel(null);
    setError(null);
    setReady(false);

    if (!src) {
      setLoading(false);
      return;
    }

    const controller = new AbortController();
    const { signal } = controller;
    let cancelled = false;

    // 原本が届くまでの繋ぎとして Lambda peaks を先に描く
    if (fallbackPeaksUrl) {
      fetchPeaksJson(fallbackPeaksUrl, signal)
        .then((lowResPeaks) => {
          if (!cancelled && !loadedRef.current) setPeaks(lowResPeaks);
        })
        .catch(() => {
          /* 先出し表示なので失敗しても本経路に影響させない */
        });
    }

    const fetchWhole = async (): Promise<ArrayBuffer> => {
      const response = await fetch(src, { signal });
      if (!response.ok) throw new Error(`原本の取得に失敗しました (${response.status})`);
      return response.arrayBuffer();
    };

    const applyWavFull = (fileBytes: ArrayBuffer, header: WavHeader) => {
      loadedRef.current = {
        mode: 'wav-full',
        header,
        fileBytes,
        audioBuffer: null,
        url: src,
      };
      setPeaks(peaksFromPcm(fileBytes, header, INSPECTOR_PAIRS_PER_SECOND));
      setDuration(header.duration);
      setMode('wav-full');
      setFormatLabel(wavFormatLabel(header));
      setReady(true);
    };

    const load = async () => {
      setLoading(true);
      try {
        const probe = await fetch(src, {
          headers: { Range: `bytes=0-${WAV_HEADER_PROBE_BYTES - 1}` },
          signal,
        });
        if (!probe.ok) throw new Error(`原本の取得に失敗しました (${probe.status})`);
        const probeBytes = await probe.arrayBuffer();
        const declaredTotal = readTotalSize(probe);
        const header = parseWavHeader(probeBytes);

        if (header) {
          // Content-Range が読めなくても、WAV なら data チャンク長で実サイズが確定する
          const totalSize = declaredTotal ?? header.dataOffset + header.dataLength;
          const hasWholeFile = probeBytes.byteLength >= totalSize;
          if (!hasWholeFile && totalSize > WAV_FULL_FETCH_MAX_BYTES) {
            loadedRef.current = {
              mode: 'wav-range',
              header,
              fileBytes: null,
              audioBuffer: null,
              url: src,
            };
            setDuration(header.duration);
            setMode('wav-range');
            setFormatLabel(wavFormatLabel(header));
            setReady(true);
            return;
          }
          const fileBytes = hasWholeFile ? probeBytes : await fetchWhole();
          if (cancelled) return;
          // 全体を読めたので data チャンクの実長でヘッダを取り直す
          applyWavFull(fileBytes, parseWavHeader(fileBytes) ?? header);
          return;
        }

        // 非 WAV は decode 経路。原本のサンプルレートは保てないため再エンコードになる
        const tooLargeToDecode = new Error(
          'ファイルが大きすぎるため範囲書き出しに対応できません',
        );
        if (declaredTotal != null && declaredTotal > DECODE_MAX_BYTES) throw tooLargeToDecode;
        // サイズ不明なら取得してから実バイト数で判断する
        const fileBytes =
          declaredTotal != null && probeBytes.byteLength >= declaredTotal
            ? probeBytes
            : await fetchWhole();
        if (cancelled) return;
        if (fileBytes.byteLength > DECODE_MAX_BYTES) throw tooLargeToDecode;

        // bext / iXML が大きい WAV は先読み分に data チャンクが入らずヘッダを
        // 読み損ねる。全体が揃った今なら読めるので、無変換の経路へ戻す
        const lateHeader = parseWavHeader(fileBytes);
        if (lateHeader) {
          applyWavFull(fileBytes, lateHeader);
          return;
        }

        const context = new AudioContext();
        let audioBuffer: AudioBuffer;
        try {
          audioBuffer = await context.decodeAudioData(fileBytes.slice(0));
        } finally {
          void context.close();
        }
        if (cancelled) return;
        loadedRef.current = {
          mode: 'decoded',
          header: null,
          fileBytes: null,
          audioBuffer,
          url: src,
        };
        setPeaks(computePeaksFromAudioBuffer(audioBuffer, INSPECTOR_PAIRS_PER_SECOND));
        setDuration(audioBuffer.duration);
        setMode('decoded');
        setFormatLabel(
          `${audioBuffer.sampleRate / 1000}kHz / ${audioBuffer.numberOfChannels}ch（再エンコード）`,
        );
        setReady(true);
      } catch (e) {
        if (cancelled || signal.aborted) return;
        // fetch の TypeError は CORS 拒否・名前解決失敗などネットワーク層の失敗
        setError(
          e instanceof TypeError
            ? '原本を取得できませんでした（配信元の CORS 設定を確認してください）'
            : e instanceof Error
              ? e.message
              : '原本の読み込みに失敗しました',
        );
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();

    return () => {
      cancelled = true;
      controller.abort();
      loadedRef.current = null;
    };
  }, [src, fallbackPeaksUrl]);

  const estimateBytes = useCallback((startSeconds: number, endSeconds: number): number => {
    const loaded = loadedRef.current;
    if (!loaded) return 0;
    if (loaded.header) {
      const frames =
        frameAt(loaded.header, endSeconds) - frameAt(loaded.header, startSeconds);
      return WAV_HEADER_BYTES + Math.max(0, frames) * loaded.header.blockAlign;
    }
    if (loaded.audioBuffer) {
      const frames = Math.max(
        0,
        Math.round((endSeconds - startSeconds) * loaded.audioBuffer.sampleRate),
      );
      return (
        WAV_HEADER_BYTES +
        frames * loaded.audioBuffer.numberOfChannels * (DECODED_EXPORT_BIT_DEPTH / 8)
      );
    }
    return 0;
  }, []);

  const sliceToWav = useCallback(
    async (startSeconds: number, endSeconds: number): Promise<Blob | null> => {
      const loaded = loadedRef.current;
      if (!loaded || endSeconds <= startSeconds) return null;

      if (loaded.mode === 'wav-full' && loaded.fileBytes && loaded.header) {
        return sliceWavFromFile(loaded.fileBytes, loaded.header, startSeconds, endSeconds);
      }

      if (loaded.mode === 'wav-range' && loaded.header) {
        const range = rangeForSelection(loaded.header, startSeconds, endSeconds);
        if (range.byteLength <= 0) return null;
        const response = await fetch(loaded.url, {
          headers: { Range: `bytes=${range.start}-${range.end}` },
        });
        if (!response.ok) throw new Error(`切り出しの取得に失敗しました (${response.status})`);
        const pcm = await response.arrayBuffer();
        return buildWavBlobFromPcm(pcm, loaded.header);
      }

      if (loaded.mode === 'decoded' && loaded.audioBuffer) {
        return sliceAudioBufferToWav(
          loaded.audioBuffer,
          startSeconds,
          endSeconds,
          DECODED_EXPORT_BIT_DEPTH,
        );
      }
      return null;
    },
    [],
  );

  // 呼び出し側は「選択が変わったら切り出す」といった副作用の依存にこの戻り値を
  // 使うため、内容が変わらない限り参照を保つ
  return useMemo(
    () => ({
      peaks,
      duration,
      mode,
      formatLabel,
      loading,
      error,
      ready,
      estimateBytes,
      sliceToWav,
    }),
    [peaks, duration, mode, formatLabel, loading, error, ready, estimateBytes, sliceToWav],
  );
}
